import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getPasswordRequirementStatuses } from '@/lib/passwordPolicy';

interface PasswordRequirementsChecklistProps {
  password: string;
  isVisible?: boolean;
  className?: string;
}

function RequirementItem({ label, isMet }: { label: string; isMet: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {isMet ? (
        <Check className="h-3.5 w-3.5 text-green-500" aria-hidden />
      ) : (
        <X className="h-3.5 w-3.5 text-destructive" aria-hidden />
      )}
      <span
        className={cn(
          'text-sm',
          isMet ? 'font-medium text-green-600' : 'text-muted-foreground'
        )}
      >
        {label}
      </span>
    </div>
  );
}

export function PasswordRequirementsChecklist({
  password,
  isVisible = true,
  className,
}: PasswordRequirementsChecklistProps) {
  if (!isVisible) return null;

  const requirements = getPasswordRequirementStatuses(password);
  const allMet = requirements.every((requirement) => requirement.isMet);

  return (
    <div
      className={cn(
        'mt-1 rounded-md border p-2 transition-all duration-200',
        allMet ? 'border-green-500/30 bg-green-500/10' : 'border-border bg-muted',
        className
      )}
      aria-live="polite"
    >
      <div className="flex flex-col gap-1">
        {requirements.map((requirement) => (
          <RequirementItem
            key={requirement.key}
            label={requirement.label}
            isMet={requirement.isMet}
          />
        ))}
      </div>
      {allMet && (
        <p className="mt-2 text-xs font-medium text-green-600">
          All password requirements met
        </p>
      )}
    </div>
  );
}
